import { useCallback, useEffect, useState, type CSSProperties } from "react";
import type { GroupInfo, GroupMaterialParams } from "../../../shared/api-contract";
import { useViewer } from "../../../state/ViewerProvider";
import { useDomainState, useEditorStore } from "../../../state/editor/useEditor";
import { Icon } from "../../kit";

/** Metal → its base colour; gem → a diamond-ish gradient; unknown → grey. */
function chipStyle(m: GroupMaterialParams | null | undefined): CSSProperties {
  if (m && m.kind === "metal") return { background: m.color };
  if (m && m.kind === "gem") return { background: "linear-gradient(135deg,#eaf4ff,#bcd8f2 55%,#8fb6dd)" };
  return { background: "#d4d8de" };
}

/**
 * The model's parts, as a flat list with a material chip each.
 *
 * Ported from the Canvas chrome, which is the only place this ever existed.
 * Without it the sole way to select a part in this chrome was clicking the mesh
 * in the 3D viewport — Picking › Hierarchy is a scene-NODE tree, fourteenth in
 * its tab and collapsed, and it answers a different question.
 *
 * Selecting here writes both the viewer's selection and the store's `selection`
 * + `material` slices, which is what makes the five material panels above
 * appear and what the right rail's Material section reads.
 */
export function PartsPanel() {
  const { client } = useViewer();
  const r = client.ring;
  const store = useEditorStore();
  const selection = useDomainState("selection");
  const models = useDomainState("models");

  const [parts, setParts] = useState<GroupInfo[]>([]);
  const [mats, setMats] = useState<Record<string, GroupMaterialParams | null>>({});
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    const gs = await r.listGroups();
    setParts(gs);
    const entries = await Promise.all(gs.map(async (g) => [g.id, await r.getGroupMaterialParams(g.id)] as const));
    setMats(Object.fromEntries(entries));
  }, [r]);

  // Re-pull on model change AND on selection change: applying a material from
  // one of the panels above changes a chip, and the chips are the only place
  // that shows it without reopening the panel.
  useEffect(() => {
    let alive = true;
    setBusy(true);
    void refresh()
      .catch(() => {})
      .finally(() => { if (alive) setBusy(false); });
    return () => { alive = false; };
  }, [refresh, models.length, selection]);

  const selectPart = (id: string) => {
    void r.selectGroup(id);
    store.setState("selection", id);
    void r.getGroupMaterialParams(id).then((p) => store.setState("material", p));
  };

  return (
    <div className="flex flex-col gap-2 p-3">
      <div className="flex items-center gap-1.5">
        <span className="text-[12px] font-semibold text-ed-text-2">Parts</span>
        {busy ? <Icon name="progress_activity" size={13} className="animate-spin text-ed-text-3" /> : null}
      </div>

      {parts.length === 0 ? (
        <p className="text-[12px] text-ed-text-3">No parts yet — load a model.</p>
      ) : (
        <div className="flex flex-col gap-0.5">
          {parts.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => selectPart(p.id)}
              className={`flex items-center gap-2 rounded px-2 py-1.5 text-left text-[12px] transition-colors ${
                selection === p.id
                  ? "bg-ed-active text-ed-text"
                  : "text-ed-text hover:bg-ed-hover"
              }`}
            >
              <span className="min-w-0 flex-1 truncate">{p.label}</span>
              <span
                className="h-4 w-4 shrink-0 rounded-full border border-ed-border"
                style={chipStyle(mats[p.id])}
              />
            </button>
          ))}
        </div>
      )}

      <p className="flex items-start gap-1.5 text-[11px] leading-snug text-ed-text-3">
        <Icon name="info" size={13} className="mt-px shrink-0" />
        Pick a part, then choose its surface from Metals, Gems, Ceramics, Pearls or Other above.
      </p>
    </div>
  );
}
